"use client";

import * as React from "react";
import { HexColorPicker } from "react-colorful";

import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Input } from "./input";

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

const ColorPicker = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  ({ value, onChange, className, disabled }, ref) => {
    const [inputValue, setInputValue] = React.useState(value);

    React.useEffect(() => {
      setInputValue(value);
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
        onChange(newValue);
      }
    };

    const handleInputBlur = () => {
      if (!/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
        setInputValue(value);
      }
    };

    return (
      <div ref={ref} className={cn("flex gap-2", className)}>
        <Popover>
          <PopoverTrigger asChild disabled={disabled}>
            <button
              type="button"
              className={cn(
                "h-10 w-10 rounded-md border border-input shrink-0",
                disabled && "cursor-not-allowed opacity-50"
              )}
              style={{ backgroundColor: value }}
              aria-label="Pick color"
            />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <HexColorPicker color={value} onChange={onChange} />
          </PopoverContent>
        </Popover>
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          placeholder="#000000"
          className="font-mono"
        />
      </div>
    );
  }
);
ColorPicker.displayName = "ColorPicker";

export { ColorPicker, type ColorPickerProps };
